import React, { useState, useEffect } from 'react';
import { database, ref, get, update } from '../../firebase';
import {
  TrendingUp, 
  Filter, 
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Search,
  Shield,
  ShieldOff,
  PauseCircle,
  PlayCircle,
  DollarSign,
  Calendar,
  X,
  AlertCircle,
  Check,
  Users,
  User
} from 'lucide-react';

const AUSTRALIA_TIMEZONE = 'Asia/Dubai';

const ROIReport = () => {
  const [roiData, setRoiData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [stats, setStats] = useState({
    totalDailyROI: 0,
    activeSchedules: 0,
    todayROI: 0,
    pausedSchedules: 0,
    blockedSchedules: 0
  });
  const [expandedRows, setExpandedRows] = useState([]);
  const [processingAction, setProcessingAction] = useState(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    fetchROIData();
  }, []);

  const getDateKeyInTimezone = (date, timezone = AUSTRALIA_TIMEZONE) => {
    return new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).format(date);
  };

  const parseDateKeyToAESTRunDate = (dateKey) => {
    const [year, month, day] = (dateKey || '').split('-').map(Number);
    if (!year || !month || !day) return null;
    // GST midnight = previous day 20:00 UTC
    return new Date(Date.UTC(year, month - 1, day, 0, 0, 0) - (4 * 60 * 60 * 1000));
  };

  const fetchROIData = async () => {
    try {
      setLoading(true);
      
      const roiRef = ref(database, 'ROISchedule');
      const snapshot = await get(roiRef);
      
      const roiArray = [];
      let totalDailyROI = 0;
      let activeSchedules = 0;
      let pausedSchedules = 0;
      let blockedSchedules = 0;
      let todayROI = 0;
      const today = getDateKeyInTimezone(new Date(), AUSTRALIA_TIMEZONE);
      
      if (snapshot.exists()) {
        const data = snapshot.val();
        Object.entries(data).forEach(([date, users]) => {
          Object.entries(users).forEach(([userId, schedules]) => {
            Object.entries(schedules).forEach(([scheduleId, schedule]) => {
              const dailyROIAmount = parseFloat(schedule.dailyROIAmount) || (
                (parseFloat(schedule.investmentAmount) || 0) * (parseFloat(schedule.dailyROI) || 0) / 100
              );
              
              // Get roiStatus from schedule - either top level or from blockStatus
              let roiStatus = schedule.roiStatus || 'active';
              
              // Also check blockStatus if it exists
              if (schedule.blockStatus) {
                if (schedule.blockStatus.isROIBlocked || 
                    schedule.blockStatus.userROIBlocked || 
                    schedule.blockStatus.packageROIBlocked || 
                    schedule.blockStatus.roiScheduleBlocked) {
                  roiStatus = 'blocked';
                }
              }
              
              const roiItem = {
                id: scheduleId,
                date: date,
                userId: schedule.userId || userId,
                userName: schedule.userName || 'Unknown',
                packageName: schedule.packageName,
                investmentAmount: schedule.investmentAmount,
                dailyROI: schedule.dailyROI,
                dailyROIAmount: dailyROIAmount,
                roiStatus: roiStatus,
                status: schedule.status || 'active',
                startDate: schedule.startDate,
                nextRoiDate: schedule.nextRoiDate,
                nextRoiDateKey: schedule.nextRoiDateKey || null,
                createdAt: schedule.createdAt || schedule.startDate,
                originalPath: `ROISchedule/${date}/${userId}/${scheduleId}`,
                buyerSponsorId: schedule.buyerSponsorId,
                downlineSponsorId: schedule.downlineSponsorId || schedule.buyerSponsorId,
                downlineSponsorName: schedule.downlineSponsorName || schedule.userName,
                // Include blockStatus for reference
                blockStatus: schedule.blockStatus || {
                  isROIBlocked: roiStatus === 'blocked',
                  userROIBlocked: roiStatus === 'blocked',
                  packageROIBlocked: roiStatus === 'blocked',
                  roiScheduleBlocked: roiStatus === 'blocked'
                }
              };
              
              roiArray.push(roiItem);
              
              if (roiStatus === 'active') {
                totalDailyROI += dailyROIAmount;
                activeSchedules++;
              } else if (roiStatus === 'paused') {
                pausedSchedules++;
              } else if (roiStatus === 'blocked') {
                blockedSchedules++;
              }
              
              const scheduleDueDate = schedule.nextRoiDateKey || date;
              if (scheduleDueDate === today && roiStatus === 'active') {
                todayROI += dailyROIAmount;
              }
            });
          });
        });
      }
      
      roiArray.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      
      setRoiData(roiArray);
      setStats({
        totalDailyROI,
        activeSchedules,
        todayROI,
        pausedSchedules,
        blockedSchedules
      });
    } catch (error) {
      console.error('Error fetching ROI data:', error);
    } finally {
      setLoading(false);
    }
  };

  const showConfirmation = (roi, action) => {
    setConfirmData({
      ...roi,
      action: action
    });
    setShowConfirmDialog(true);
  };

// In ROIReport.js - Update the handleActionConfirm function

const handleActionConfirm = async () => {
  if (!confirmData) return;
  
  try {
    setProcessingAction(confirmData.id);
    
    let newStatus = confirmData.roiStatus;
    switch(confirmData.action) {
      case 'pause':
        newStatus = 'paused';
        break;
      case 'resume':
        newStatus = 'active';
        break;
      case 'block':
        newStatus = 'blocked';
        break;
      case 'unblock':
        newStatus = 'active';
        break;
    }
    
    const timestamp = new Date().toISOString();
    
    // Prepare updates - Update roiStatus and add blockCheck to schedule
    const updates = {
      roiStatus: newStatus,
      lastUpdated: timestamp,
      updatedBy: 'admin'
    };
    
    // CRITICAL: Add blockCheck to ROISchedule
    if (confirmData.action === 'block') {
      updates['blockCheck'] = {
        buyerBlocked: false,
        targetBlocked: true,
        directUplineBlocked: false,
        roiBlocked: true,
        blockedAt: timestamp,
        blockedBy: 'admin',
        blockReason: 'ROI blocked by admin from ROI Report'
      };
      
      // Also update blockStatus for backward compatibility
      updates['blockStatus/isROIBlocked'] = true;
      updates['blockStatus/userROIBlocked'] = true;
      updates['blockStatus/packageROIBlocked'] = true;
      updates['blockStatus/roiScheduleBlocked'] = true;
      updates['blockStatus/blockReasons'] = ['ROI blocked by admin from ROI Report'];
      updates['blockStatus/blockedAt'] = timestamp;
      updates['blockStatus/blockedBy'] = 'admin';
    } else if (confirmData.action === 'unblock') {
      updates['blockCheck'] = {
        buyerBlocked: false,
        targetBlocked: false,
        directUplineBlocked: false,
        roiBlocked: false,
        unblockedAt: timestamp,
        unblockedBy: 'admin'
      };
      
      // Also update blockStatus for backward compatibility
      updates['blockStatus/isROIBlocked'] = false;
      updates['blockStatus/userROIBlocked'] = false;
      updates['blockStatus/packageROIBlocked'] = false;
      updates['blockStatus/roiScheduleBlocked'] = false;
      updates['blockStatus/blockReasons'] = [];
      updates['blockStatus/unblockedAt'] = timestamp;
      updates['blockStatus/unblockedBy'] = 'admin';
    } else if (confirmData.action === 'pause') {
      updates['blockStatus/isPaused'] = true;
      updates['blockStatus/pausedAt'] = timestamp;
      updates['blockStatus/pausedBy'] = 'admin';
    } else if (confirmData.action === 'resume') {
      updates['blockStatus/isPaused'] = false;
      updates['blockStatus/resumedAt'] = timestamp;
      updates['blockStatus/resumedBy'] = 'admin';
    }
    
    // Update ROI schedule
    await update(ref(database, confirmData.originalPath), updates);
    
    // Also update user's package blockCheck object to stay in sync
    try {
      // Try to find and update user's package
      const usersRef = ref(database, 'users');
      const usersSnapshot = await get(usersRef);
      
      if (usersSnapshot.exists()) {
        const usersData = usersSnapshot.val();
        
        // Search through all dates
        for (const dateKey in usersData) {
          for (const userKey in usersData[dateKey]) {
            const user = usersData[dateKey][userKey];
            
            if (user && (user.userId === confirmData.userId || user.sponsorId === confirmData.userId)) {
              // Found the user, update their package roiStatus AND blockCheck
              if (user.packages && Array.isArray(user.packages)) {
                const packageIndex = user.packages.findIndex(p => 
                  p.packageName === confirmData.packageName || 
                  p.transactionId === confirmData.id.replace('_ROI', '')
                );
                
                if (packageIndex !== -1) {
                  const userPackagePath = `users/${dateKey}/${userKey}/packages/${packageIndex}`;
                  
                  // Get current package to preserve existing blockCheck fields
                  const packageSnapshot = await get(ref(database, userPackagePath));
                  const currentPackage = packageSnapshot.val() || {};
                  
                  // Prepare package updates
                  const packageUpdates = {
                    roiStatus: newStatus,
                    roiStatusUpdated: timestamp,
                    roiStatusUpdatedBy: 'admin'
                  };
                  
                  // Update blockCheck in user package
                  if (confirmData.action === 'block') {
                    packageUpdates['blockCheck'] = {
                      ...(currentPackage.blockCheck || {}),
                      buyerBlocked: currentPackage.blockCheck?.buyerBlocked || false,
                      targetBlocked: true,
                      directUplineBlocked: currentPackage.blockCheck?.directUplineBlocked || false,
                      roiBlocked: true,
                      blockedAt: timestamp,
                      blockedBy: 'admin',
                      blockReason: 'ROI blocked by admin from ROI Report'
                    };
                  } else if (confirmData.action === 'unblock') {
                    packageUpdates['blockCheck'] = {
                      ...(currentPackage.blockCheck || {}),
                      buyerBlocked: currentPackage.blockCheck?.buyerBlocked || false,
                      targetBlocked: false,
                      directUplineBlocked: currentPackage.blockCheck?.directUplineBlocked || false,
                      roiBlocked: false,
                      unblockedAt: timestamp,
                      unblockedBy: 'admin'
                    };
                  }
                  
                  await update(ref(database, userPackagePath), packageUpdates);
                  console.log('User package blockCheck updated');
                }
              }
              break; // Found user, exit loop
            }
          }
        }
      }
    } catch (err) {
      console.log('Error updating user package:', err);
      // Continue even if user package update fails
    }
    
    // Update local state
    setRoiData(prev => prev.map(item => {
      if (item.id === confirmData.id) {
        return { 
          ...item, 
          roiStatus: newStatus,
          blockCheck: confirmData.action === 'block' 
            ? { buyerBlocked: false, targetBlocked: true, directUplineBlocked: false, roiBlocked: true }
            : confirmData.action === 'unblock'
            ? { buyerBlocked: false, targetBlocked: false, directUplineBlocked: false, roiBlocked: false }
            : item.blockCheck,
          blockStatus: {
            ...item.blockStatus,
            isROIBlocked: confirmData.action === 'block',
            userROIBlocked: confirmData.action === 'block',
            packageROIBlocked: confirmData.action === 'block',
            roiScheduleBlocked: confirmData.action === 'block',
            isPaused: confirmData.action === 'pause'
          }
        };
      }
      return item;
    }));
    
    // Update stats
    fetchROIData();
    
    // Show success
    const actionNames = {
      'pause': 'Paused',
      'resume': 'Resumed',
      'block': 'Blocked',
      'unblock': 'Unblocked'
    };
    setSuccessMessage(
      `${actionNames[confirmData.action]} ROI schedule successfully!`
    );
    setShowSuccessDialog(true);
    
  } catch (error) {
    console.error('Error updating ROI status:', error);
  } finally {
    setProcessingAction(null);
    setShowConfirmDialog(false);
    setConfirmData(null);
  }
};

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      timeZone: AUSTRALIA_TIMEZONE,
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatNextRoiDate = (item) => {
    const formatBoth = (dateObj) => {
      const australiaDate = dateObj.toLocaleDateString('en-US', {
        timeZone: AUSTRALIA_TIMEZONE,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
      const australiaTime = dateObj.toLocaleTimeString('en-US', {
        timeZone: AUSTRALIA_TIMEZONE,
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      });
      return `${australiaDate} ${australiaTime} (Dubai)`;
    };

    if (item?.nextRoiDateKey) {
      const parsed = parseDateKeyToAESTRunDate(item.nextRoiDateKey);
      if (parsed) return formatBoth(parsed);
      return item.nextRoiDateKey;
    }
    if (!item?.nextRoiDate) return 'N/A';
    return formatBoth(new Date(item.nextRoiDate));
  };

  const getStatusBadge = (status) => {
    switch(status) {
      case 'active':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'paused':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'blocked':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const filterData = () => {
    let filtered = roiData;
    
    if (dateFilter !== 'all') {
      const now = new Date();
      const australiaNow = new Date(now.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date || item.createdAt);
        const australiaItemDate = new Date(itemDate.toLocaleString('en-US', { timeZone: AUSTRALIA_TIMEZONE }));
        switch(dateFilter) {
          case 'today':
            return australiaItemDate.toDateString() === australiaNow.toDateString();
          case 'week':
            const weekAgo = new Date(australiaNow);
            weekAgo.setDate(weekAgo.getDate() - 7);
            return australiaItemDate > weekAgo;
          case 'month':
            const monthAgo = new Date(australiaNow);
            monthAgo.setDate(monthAgo.getDate() - 30);
            return australiaItemDate > monthAgo;
          default:
            return true;
        }
      });
    }
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.roiStatus === statusFilter);
    }
    
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.userName?.toLowerCase().includes(term) ||
        item.userId?.toLowerCase().includes(term) ||
        item.packageName?.toLowerCase().includes(term) ||
        item.downlineSponsorName?.toLowerCase().includes(term) ||
        item.downlineSponsorId?.toLowerCase().includes(term)
      );
    }
    
    return filtered;
  };

  const filteredData = filterData();

  const toggleRowExpansion = (id) => {
    setExpandedRows(prev =>
      prev.includes(id)
        ? prev.filter(rowId => rowId !== id)
        : [...prev, id]
    );
  };

  const Dialog = ({ isOpen, onClose, children, title }) => {
    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
        <div className="bg-gray-900 border border-gray-700 rounded-2xl shadow-2xl max-w-md w-full">
          <div className="flex items-center justify-between p-6 border-b border-gray-800">
            <h3 className="text-xl font-bold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
              disabled={processingAction}
            >
              <X size={24} />
            </button>
          </div>
          <div className="p-6">
            {children}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      <h1 className="text-2xl font-bold text-white mb-2">ROI Report</h1>
      <p className="text-gray-400 mb-6">ROI schedules management and tracking</p>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-6">
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Daily ROI</div>
          <div className="text-lg font-bold text-white">{formatCurrency(stats.totalDailyROI)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Active Schedules</div>
          <div className="text-lg font-bold text-green-400">{stats.activeSchedules}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Today's ROI</div>
          <div className="text-lg font-bold text-blue-400">{formatCurrency(stats.todayROI)}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Paused</div>
          <div className="text-lg font-bold text-yellow-400">{stats.pausedSchedules}</div>
        </div>
        <div className="card-glow p-3 bg-gray-800/50 rounded-xl">
          <div className="text-gray-400 text-xs mb-1">Blocked</div>
          <div className="text-lg font-bold text-red-400">{stats.blockedSchedules}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="card-glow p-4 mb-6 bg-gray-800/50 rounded-xl">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by user, package or sponsor"
                className="w-full pl-10 pr-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div className="flex flex-wrap gap-2">
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Dates</option>
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
            </select>
            
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="blocked">Blocked</option>
            </select>
            
            <button
              onClick={fetchROIData}
              className="px-4 py-2 bg-primary hover:bg-primary/80 rounded-lg text-white flex items-center gap-2"
            >
              <RefreshCw className="h-4 w-4" />
              <span className="hidden sm:inline">Refresh</span>
            </button>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="hidden lg:block card-glow overflow-hidden mb-6 bg-gray-800/50 rounded-xl">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr className="text-sm">
                <th className="py-3 px-4 text-left text-gray-400">User</th>
                <th className="py-3 px-4 text-left text-gray-400">Downline Sponsor</th>
                <th className="py-3 px-4 text-left text-gray-400">Package</th>
                <th className="py-3 px-4 text-left text-gray-400">Investment</th>
                <th className="py-3 px-4 text-left text-gray-400">Daily ROI</th>
                <th className="py-3 px-4 text-left text-gray-400">Next Date</th>
                <th className="py-3 px-4 text-left text-gray-400">Status</th>
                <th className="py-3 px-4 text-left text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700/30">
              {filteredData.map((item) => {
                const isPaused = item.roiStatus === 'paused';
                const isBlocked = item.roiStatus === 'blocked';
                const isActive = item.roiStatus === 'active';
                
                return (
                  <tr key={item.id} className="hover:bg-gray-700/20">
                    <td className="py-3 px-4">
                      <div className="text-white font-medium">{item.userName}</div>
                      <div className="text-xs text-gray-400">ID: {item.userId}</div>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <Users className="h-3 w-3 text-blue-400" />
                        <div>
                          <div className="text-white text-sm">{item.downlineSponsorName || item.userName}</div>
                          <div className="text-xs text-gray-400">ID: {item.downlineSponsorId || item.userId}</div>
                        </div>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-white text-sm">{item.packageName}</td>
                    <td className="py-3 px-4 text-white font-bold">
                      {formatCurrency(item.investmentAmount)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`font-bold ${
                        isBlocked ? 'text-red-400' : 
                        isPaused ? 'text-yellow-400' : 
                        'text-green-400'
                      }`}>
                        {formatCurrency(item.dailyROIAmount)}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-300 text-sm">
                      {formatNextRoiDate(item)}
                    </td>
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center px-2 py-1 rounded text-xs border ${getStatusBadge(item.roiStatus)}`}>
                        {item.roiStatus}
                      </span>
                      {item.blockStatus && item.blockStatus.isROIBlocked && (
                        <span className="ml-1 inline-flex items-center px-1 py-0.5 rounded text-[8px] bg-red-500/20 text-red-400 border border-red-500/30">
                          Blocked
                        </span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex gap-2">
                        {isPaused ? (
                          <button
                            onClick={() => showConfirmation(item, 'resume')}
                            disabled={processingAction === item.id}
                            className="px-3 py-1.5 text-xs rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 flex items-center gap-1"
                          >
                            <PlayCircle className="h-3.5 w-3.5" />
                            Resume
                          </button>
                        ) : isActive ? (
                          <button
                            onClick={() => showConfirmation(item, 'pause')}
                            disabled={processingAction === item.id}
                            className="px-3 py-1.5 text-xs rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30 flex items-center gap-1"
                          >
                            <PauseCircle className="h-3.5 w-3.5" />
                            Pause
                          </button>
                        ) : null}
                        
                        {!isBlocked ? (
                          <button
                            onClick={() => showConfirmation(item, 'block')}
                            disabled={processingAction === item.id}
                            className="px-3 py-1.5 text-xs rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 flex items-center gap-1"
                          >
                            <Shield className="h-3.5 w-3.5" />
                            Block
                          </button>
                        ) : (
                          <button
                            onClick={() => showConfirmation(item, 'unblock')}
                            disabled={processingAction === item.id}
                            className="px-3 py-1.5 text-xs rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 flex items-center gap-1"
                          >
                            <ShieldOff className="h-3.5 w-3.5" />
                            Unblock
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Mobile View */}
      <div className="lg:hidden space-y-4">
        {filteredData.map((item) => {
          const isPaused = item.roiStatus === 'paused';
          const isBlocked = item.roiStatus === 'blocked';
          const isActive = item.roiStatus === 'active';
          const isExpanded = expandedRows.includes(item.id);
          
          return (
            <div key={item.id} className="card-glow overflow-hidden bg-gray-800/50 rounded-xl">
              <button
                onClick={() => toggleRowExpansion(item.id)}
                className="w-full p-4 flex justify-between items-center hover:bg-gray-700/50"
              >
                <div className="text-left flex-1">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="font-medium text-white">{item.userName}</div>
                      <div className="text-xs text-gray-400">{item.packageName}</div>
                      <div className="flex items-center gap-1 mt-1">
                        <Users className="h-3 w-3 text-blue-400" />
                        <span className="text-xs text-gray-400">Sponsor: {item.downlineSponsorName || item.userName}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`font-bold ${
                        isBlocked ? 'text-red-400' : 
                        isPaused ? 'text-yellow-400' : 
                        'text-green-400'
                      }`}>
                        {formatCurrency(item.dailyROIAmount)}
                      </div>
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs border mt-1 ${getStatusBadge(item.roiStatus)}`}>
                        {item.roiStatus}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="ml-2">
                  {isExpanded ? (
                    <ChevronUp className="h-4 w-4 text-primary" />
                  ) : (
                    <ChevronDown className="h-4 w-4 text-primary" />
                  )}
                </div>
              </button>
             
              {isExpanded && (
                <div className="p-4 border-t border-gray-700">
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Investment</div>
                        <div className="text-white text-sm">{formatCurrency(item.investmentAmount)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Next Date</div>
                        <div className="text-white text-sm">{formatNextRoiDate(item)}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">User ID</div>
                        <div className="text-white text-sm">{item.userId}</div>
                      </div>
                      <div>
                        <div className="text-gray-400 text-xs mb-1">Sponsor ID</div>
                        <div className="text-white text-sm">{item.downlineSponsorId || item.userId}</div>
                      </div>
                    </div>
                    
                    {/* Block Status Details */}
                    {item.blockStatus && (
                      <div className="bg-gray-900/50 p-3 rounded-lg">
                        <div className="text-xs text-gray-400 mb-2">Block Status Details:</div>
                        <div className="grid grid-cols-2 gap-2 text-xs">
                          <div>
                            <span className="text-gray-500">isROIBlocked:</span>
                            <span className={`ml-1 ${item.blockStatus.isROIBlocked ? 'text-red-400' : 'text-green-400'}`}>
                              {item.blockStatus.isROIBlocked ? 'true' : 'false'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">userROIBlocked:</span>
                            <span className={`ml-1 ${item.blockStatus.userROIBlocked ? 'text-red-400' : 'text-green-400'}`}>
                              {item.blockStatus.userROIBlocked ? 'true' : 'false'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">packageROIBlocked:</span>
                            <span className={`ml-1 ${item.blockStatus.packageROIBlocked ? 'text-red-400' : 'text-green-400'}`}>
                              {item.blockStatus.packageROIBlocked ? 'true' : 'false'}
                            </span>
                          </div>
                          <div>
                            <span className="text-gray-500">roiScheduleBlocked:</span>
                            <span className={`ml-1 ${item.blockStatus.roiScheduleBlocked ? 'text-red-400' : 'text-green-400'}`}>
                              {item.blockStatus.roiScheduleBlocked ? 'true' : 'false'}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="pt-3 border-t border-gray-700/30">
                      <div className="grid grid-cols-2 gap-2">
                        {isPaused ? (
                          <button
                            onClick={() => showConfirmation(item, 'resume')}
                            disabled={processingAction === item.id}
                            className="py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 flex items-center justify-center gap-2"
                          >
                            <PlayCircle className="h-4 w-4" />
                            Resume
                          </button>
                        ) : isActive ? (
                          <button
                            onClick={() => showConfirmation(item, 'pause')}
                            disabled={processingAction === item.id}
                            className="py-2 rounded-lg bg-yellow-500/20 hover:bg-yellow-500/30 text-yellow-400 border border-yellow-500/30 flex items-center justify-center gap-2"
                          >
                            <PauseCircle className="h-4 w-4" />
                            Pause
                          </button>
                        ) : null}
                        
                        {!isBlocked ? (
                          <button
                            onClick={() => showConfirmation(item, 'block')}
                            disabled={processingAction === item.id}
                            className="py-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/30 flex items-center justify-center gap-2"
                          >
                            <Shield className="h-4 w-4" />
                            Block
                          </button>
                        ) : (
                          <button
                            onClick={() => showConfirmation(item, 'unblock')}
                            disabled={processingAction === item.id}
                            className="py-2 rounded-lg bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/30 flex items-center justify-center gap-2"
                          >
                            <ShieldOff className="h-4 w-4" />
                            Unblock
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
        
        {filteredData.length === 0 && (
          <div className="text-center py-12 card-glow bg-gray-800/50 rounded-xl">
            <div className="text-4xl mb-4">📊</div>
            <h3 className="text-lg font-semibold text-white mb-2">No ROI Schedules</h3>
            <p className="text-gray-400">Try adjusting your search filters</p>
          </div>
        )}
      </div>

      {/* Confirmation Dialog */}
      <Dialog 
        isOpen={showConfirmDialog} 
        onClose={() => !processingAction && setShowConfirmDialog(false)}
        title="Confirm Action"
      >
        {confirmData && (
          <div className="space-y-4">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="text-red-400" size={32} />
              </div>
              <h3 className="text-lg font-bold text-white mb-2">
                {confirmData.action === 'pause' ? 'Pause ROI Schedule' : 
                 confirmData.action === 'resume' ? 'Resume ROI Schedule' : 
                 confirmData.action === 'block' ? 'Block ROI Schedule' : 
                 'Unblock ROI Schedule'}
              </h3>
              <p className="text-gray-300">
                Are you sure you want to {confirmData.action} this ROI schedule?
              </p>
            </div>
            
            <div className="bg-gray-800/50 rounded-xl p-4">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">User:</span>
                  <span className="text-white font-bold">{confirmData.userName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Downline Sponsor:</span>
                  <span className="text-white">{confirmData.downlineSponsorName || confirmData.userName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Package:</span>
                  <span className="text-white">{confirmData.packageName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Daily ROI:</span>
                  <span className="text-green-400 font-bold">{formatCurrency(confirmData.dailyROIAmount)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Current Status:</span>
                  <span className={`${confirmData.roiStatus === 'blocked' ? 'text-red-400' : 
                                     confirmData.roiStatus === 'paused' ? 'text-yellow-400' : 
                                     'text-green-400'}`}>
                    {confirmData.roiStatus}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex gap-4 mt-6">
              <button
                onClick={() => !processingAction && setShowConfirmDialog(false)}
                disabled={processingAction}
                className="flex-1 py-3 bg-gray-700 hover:bg-gray-600 disabled:opacity-50 rounded-xl text-white font-bold"
              >
                Cancel
              </button>
              <button
                onClick={handleActionConfirm}
                disabled={processingAction}
                className={`flex-1 py-3 rounded-xl text-white font-bold ${
                  confirmData.action === 'block' ? 'bg-red-500 hover:bg-red-600' :
                  confirmData.action === 'pause' ? 'bg-yellow-500 hover:bg-yellow-600' :
                  'bg-green-500 hover:bg-green-600'
                }`}
              >
                {processingAction ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        )}
      </Dialog>

      {/* Success Dialog */}
      <Dialog 
        isOpen={showSuccessDialog} 
        onClose={() => setShowSuccessDialog(false)}
        title="Success"
      >
        <div className="text-center">
          <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="text-green-400" size={32} />
          </div>
          <h3 className="text-lg font-bold text-white mb-2">Action Completed!</h3>
          <p className="text-gray-300 mb-6">{successMessage}</p>
          <button
            onClick={() => setShowSuccessDialog(false)}
            className="w-full py-3 bg-primary hover:bg-primary/80 rounded-xl text-white font-bold"
          >
            OK
          </button>
        </div>
      </Dialog>
    </div>
  );
};

export default ROIReport;
